import trackingConsole from './trackingConsole';
import { isListNotEmpty, isNullOrUndefined, isStringEmpty, isStringNotEmpty } from './objectUtils';

/*
 * A paid customer pulled back off the encounter is the funnel's worst failure, and the only trace it
 * leaves is that window.location changed. The router, redux, an effect, the browser's own back
 * handling and a bfcache restore all produce the same observable, so the log has to name the caller:
 * every history mutation is recorded with the stack that made it, and verifyEncounterRedirect dumps
 * the record when it decides the redirect was lost. Recording is silent - nothing reaches LogRocket
 * until a dump asks for it - so the cost on a healthy session is one bounded array.
 */
const HISTORY_MUTATION_LIMIT = 20;
const STACK_LINE_LIMIT = 8;
const STACK_LINE_LENGTH_LIMIT = 200;
const HISTORY_MUTATION_GLOBAL_KEY = '__readyRxHistoryMutationLog';

const entries = [];

// set by whoever knows which departures matter - kept out of this module so it needs no
// knowledge of routes, and so funnelRouteUtils can own the rule without an import cycle
let observer = null;
let isInstalled = false;
let lastKnownUrl = null;

const getCurrentUrl = () => {
	if (typeof window === 'undefined' || isNullOrUndefined(window.location)) {
		return null;
	}

	return `${window.location.pathname}${window.location.search}${window.location.hash}`;
};

const toRelativeUrl = (value) => {
	if (isStringEmpty(value)) {
		return null;
	}

	try {
		const parsed = new URL(value, window.location.href);
		return `${parsed.pathname}${parsed.search}${parsed.hash}`;
	} catch (e) {
		return String(value);
	}
};

// the whole stack is kept rather than sliced past this helper: V8 prefixes an "Error" header line
// and JavaScriptCore does not, so a fixed slice would eat a real frame in exactly the browser this
// failure was reported from
const getCallerStack = () => {
	try {
		const stack = new Error().stack;
		if (isStringEmpty(stack)) {
			return null;
		}

		return stack
			.split('\n')
			.slice(0, STACK_LINE_LIMIT)
			.map((line) => line.trim().slice(0, STACK_LINE_LENGTH_LIMIT))
			.join(' | ');
	} catch (e) {
		return null;
	}
};

const getElapsedMs = () => {
	try {
		return typeof performance !== 'undefined' ? Math.round(performance.now()) : null;
	} catch (e) {
		return null;
	}
};

const recordMutation = (type, previousUrl, stack, detail = null) => {
	try {
		const currentUrl = getCurrentUrl();
		entries.push({
			type,
			at: new Date().toISOString(),
			sinceLoadMs: getElapsedMs(),
			from: previousUrl,
			to: currentUrl,
			detail,
			stack
		});

		while (entries.length > HISTORY_MUTATION_LIMIT) {
			entries.shift();
		}

		lastKnownUrl = currentUrl;

		if (typeof observer === 'function') {
			try {
				observer(entries[entries.length - 1]);
			} catch (observerError) {
				// an observer must never be what breaks a navigation it was only watching
			}
		}
	} catch (e) {
		// instrumentation must never break a navigation
	}
};

const readUrlSafely = () => {
	try {
		return getCurrentUrl();
	} catch (e) {
		return null;
	}
};

const readStackSafely = () => {
	try {
		return getCallerStack();
	} catch (e) {
		return null;
	}
};

export const getHistoryMutationLog = () => entries.map((entry) => ({ ...entry }));

export const setHistoryMutationObserver = (fn) => {
	observer = typeof fn === 'function' ? fn : null;
};

export const installHistoryMutationLog = () => {
	if (isInstalled === true || typeof window === 'undefined' || isNullOrUndefined(window.history)) {
		return;
	}

	isInstalled = true;
	lastKnownUrl = readUrlSafely();

	const originalPushState = window.history.pushState;
	const originalReplaceState = window.history.replaceState;

	// patched with function expressions so the original keeps the `this` it was called on
	window.history.pushState = function (...args) {
		const previousUrl = readUrlSafely();
		const stack = readStackSafely();
		const result = originalPushState.apply(this, args);
		recordMutation('pushState', previousUrl, stack, toRelativeUrl(args[2]));
		return result;
	};

	window.history.replaceState = function (...args) {
		const previousUrl = readUrlSafely();
		const stack = readStackSafely();
		const result = originalReplaceState.apply(this, args);
		recordMutation('replaceState', previousUrl, stack, toRelativeUrl(args[2]));
		return result;
	};

	// these fire after the URL has already changed, so the previous URL comes from the last record
	window.addEventListener('popstate', () => { recordMutation('popstate', lastKnownUrl, readStackSafely()); });
	window.addEventListener('hashchange', () => { recordMutation('hashchange', lastKnownUrl, readStackSafely()); });
	// a bfcache restore reinstates a whole earlier document, URL included, and reads to the app as a
	// silent revert with no history mutation behind it
	window.addEventListener('pageshow', (event) => { recordMutation('pageshow', lastKnownUrl, null, event?.persisted === true ? 'persisted' : 'fresh'); });

	// readable from the console during a live session, and by the repro harness. No new exposure:
	// every funnel step already pushes url: window.location.href into window.dataLayer, so the same
	// URLs are on window for any script on the page either way
	try {
		window[HISTORY_MUTATION_GLOBAL_KEY] = getHistoryMutationLog;
	} catch (e) {
		// a frozen window is not a reason to lose the log
	}
};

// one line per entry rather than one payload: LogRocket truncates a long captured message, and the
// tail of this record is the part that names the reverter
export const dumpHistoryMutationLog = (reason) => {
	try {
		const recorded = getHistoryMutationLog();
		trackingConsole.log('history mutations:', { reason, url: readUrlSafely(), count: recorded.length, isInstalled });

		if (isListNotEmpty(recorded)) {
			recorded.forEach((entry, index) => {
				trackingConsole.log(`history mutation ${index + 1}/${recorded.length}:`, entry);
			});
		} else if (isStringNotEmpty(reason)) {
			trackingConsole.log('history mutations: nothing recorded for', reason);
		}
	} catch (e) {
		// instrumentation must never break the redirect it is reporting on
	}
};
